/**
 * Note Entity - Staff and nurse notes on applications
 * 
 * Per user decision D4: Nurses see staff general notes by default.
 * Note visibility is configurable per role.
 */
import { z } from 'zod';
import { NoteId, ApplicationId, UserId } from '../types/branded';
import { NoteType, UserRole } from '../types/enums';

// ============================================================================
// Application Note Entity
// ============================================================================

export const ApplicationNoteSchema = z.object({
  id: NoteId,
  applicationId: ApplicationId,
  
  // Author info
  authorId: UserId,
  authorRole: UserRole,
  authorName: z.string().nullable().optional(),
  
  // Note content
  noteType: NoteType,
  content: z.string().min(1).max(10000),
  isImportant: z.boolean().default(false),
  
  // Visibility (per user decision D4)
  // Default: all roles can see (nurses see staff notes by default)
  visibility: z.array(UserRole).default(['staff', 'nurse', 'admin']),
  
  // Timestamps
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type ApplicationNote = z.infer<typeof ApplicationNoteSchema>;

// ============================================================================
// Input Types
// ============================================================================

export const CreateNoteInputSchema = z.object({
  applicationId: ApplicationId,
  noteType: NoteType,
  content: z.string().min(1).max(10000),
  isImportant: z.boolean().optional(),
  authorName: z.string().nullable().optional(),
  // Visibility defaults based on noteType (see config/visibility.ts)
  visibility: z.array(UserRole).optional(),
});
export type CreateNoteInput = z.infer<typeof CreateNoteInputSchema>;

export const UpdateNoteInputSchema = z.object({
  noteId: NoteId,
  content: z.string().min(1).max(10000).optional(),
  isImportant: z.boolean().optional(),
  visibility: z.array(UserRole).optional(),
});
export type UpdateNoteInput = z.infer<typeof UpdateNoteInputSchema>;

export const DeleteNoteInputSchema = z.object({
  noteId: NoteId,
});
export type DeleteNoteInput = z.infer<typeof DeleteNoteInputSchema>;
